import * as React from 'react'
import { Alert, Form, Input, Space } from 'antd'
import { useState } from 'react'
import { Container } from 'typescript-ioc/es5'
import { AppService } from '@halo/services/app.service'
import './test-page-analysis-drawer.scss'
import { IHydratedPageAnalysisConfig } from '@halo/interfaces/hydrated-page-analysis-config'
import { Button, Drawer } from '@pushly/aqe/lib/components'
import { PageAnalysisService } from '@halo/services/page-analysis.service'
import ICategoryStrategy from '@halo/interfaces/category-strategy'
import { IPageAnalysis, IPageCategory } from '@pushly/aqe/lib/interfaces'
import { VALID_URL_REGEX } from '@pushly/aqe/lib/constants'
import { CaretRightOutlined, Loading3QuartersOutlined } from '@ant-design/icons'
import PageAnalysisResultsPanel from './page-analysis-results-panel'
import CatalogExtractionResultsPanel, {
    ICatalogExtractionResult,
} from './catalog-extraction-results-panel'

interface IPageAnalysisError {
    message: string
    status_code: number | null
    reference_url: string | null
    details: Record<string, any> | null
}

interface IPageAnalysisWithCatalog extends Omit<IPageAnalysis, 'error'> {
    catalog_extraction_results?: ICatalogExtractionResult[] | null
    error?: IPageAnalysisError | null
}

export interface ITestPageAnalysisDrawerProps {
    title: string
    domainId?: number
    isPartial?: boolean
    hydratedPageAnalysis?: Partial<IHydratedPageAnalysisConfig>
    categoryStrategies?: Array<Partial<ICategoryStrategy>>
    pageCategories: IPageCategory[]
    onClose: () => void
}

function TestPageAnalysisDrawer(props: ITestPageAnalysisDrawerProps) {
    const appService = Container.get(AppService)
    const pageAnalysisService: PageAnalysisService = Container.get(PageAnalysisService)
    const [form] = Form.useForm()

    const [isRunning, setIsRunning] = useState<boolean>(false)
    const [pageAnalysis, setPageAnalysis] = useState<IPageAnalysisWithCatalog | null>(null)
    const [errMessage, setErrMessage] = useState<string | null>(null)

    function hasConfiguration() {
        return !!(props.domainId || (props.categoryStrategies || []).length > 0 || props.hydratedPageAnalysis)
    }

    async function runAnalysis() {
        setErrMessage(null)
        setIsRunning(true)

        const res = await pageAnalysisService.analyze(
            form.getFieldValue('page_url'),
            props.domainId,
            null,
            props.hydratedPageAnalysis,
            props.categoryStrategies,
        )
        if (res.error) {
            setErrMessage(res.error.toString())
            setPageAnalysis(null)
        } else {
            setErrMessage(null)
            setPageAnalysis(res.data)
        }

        setIsRunning(false)
    }

    const catalogResults = Array.isArray(pageAnalysis?.catalog_extraction_results)
        ? pageAnalysis!.catalog_extraction_results
        : null

    return (
        <Drawer
            width={800}
            getContainer={appService.getAppContainer}
            className="test-page-analysis-drawer"
            title={props.title}
            placement="right"
            onClose={props.onClose}
            hideFooter={true}
            open={hasConfiguration()}
        >
            {hasConfiguration() && (
                <Form
                    layout="horizontal"
                    form={form}
                    onFinish={runAnalysis}
                    size="small"
                    labelAlign="left"
                    requiredMark={false}
                >
                    <Form.Item
                        label="Page Url"
                        name="page_url"
                        rules={[
                            { required: true, message: 'Page Url is required' },
                            { pattern: VALID_URL_REGEX, message: 'Must be a valid url' },
                        ]}
                    >
                        <Space.Compact style={{ width: '100%' }}>
                            <Input autoComplete="off" disabled={isRunning} />
                            <Button disabled={isRunning} onClick={() => form.submit()}>
                                {isRunning ? <Loading3QuartersOutlined spin={true} /> : <CaretRightOutlined />}
                            </Button>
                        </Space.Compact>
                    </Form.Item>
                </Form>
            )}

            {errMessage && (
                <Alert
                    style={{ marginBottom: 16 }}
                    message="Fatal Execution Error"
                    description={errMessage}
                    type="error"
                    showIcon
                />
            )}

            {pageAnalysis?.error && (
                <Alert
                    style={{ marginBottom: 16 }}
                    message={`Page Error${
                        pageAnalysis.error.status_code ? ` (${pageAnalysis.error.status_code})` : ''
                    }`}
                    description={
                        <>
                            <p style={{ marginBottom: 4 }}>{pageAnalysis.error.message}</p>
                            {pageAnalysis.error.reference_url && (
                                <p style={{ marginBottom: 0 }}>
                                    <a
                                        href={pageAnalysis.error.reference_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Learn more
                                    </a>
                                </p>
                            )}
                        </>
                    }
                    type="warning"
                    showIcon
                />
            )}

            {pageAnalysis && (
                <PageAnalysisResultsPanel
                    pageAnalysis={pageAnalysis as IPageAnalysis}
                    isPartial={props.isPartial}
                    pageCategories={props.pageCategories}
                />
            )}

            {catalogResults && catalogResults.length > 0 && (
                <CatalogExtractionResultsPanel results={catalogResults} />
            )}
        </Drawer>
    )
}

export default TestPageAnalysisDrawer
